/**
=========================================================
* Material Dashboard 2  React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo } from "react";

// porp-types is a library for typechecking of props
import PropTypes from "prop-types";

// react-chartjs-2 components
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Title,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";

// @mui material components
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "~/components/MDBox";
import MDTypography from "~/components/MDTypography";

// ReportsBarChart configurations
import { Divider, Grid } from "@mui/material";
import configs from "~/examples/Charts/BarCharts/ReportsBarChart/configs";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

function ReportsBarChart({ color, title, description, date, chart }) {
  const { data, options } = configs(chart.labels || [], chart.datasets || []);

  return (
    <MDBox height="inherit">
      <Grid container spacing={2}>
        <Grid item lg={12} xl={12} md={12} xs={12} mt={6}>
          <Card sx={{ height: "100%" }}>
            <MDBox padding="1rem">
              {useMemo(
                () => (
                  <MDBox
                    variant="gradient"
                    bgColor={color}
                    borderRadius="lg"
                    coloredShadow={color}
                    py={2}
                    pr={0.5}
                    mt={-5}
                    height="20rem"
                  >
                    <Bar data={data} options={options} redraw />
                  </MDBox>
                ),
                [color, chart]
              )}
              <MDBox pb={1} px={1}>
                <MDTypography variant="h6" textTransform="capitalize">
                  {title}
                </MDTypography>
                <MDTypography
                  component="div"
                  variant="button"
                  color="text"
                  fontWeight="light"
                >
                  {description}
                </MDTypography>
                <Divider />
              </MDBox>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

// Setting default values for the props of ReportsBarChart
ReportsBarChart.defaultProps = {
  color: "info",
  description: "",
};

// Typechecking props for the ReportsBarChart
ReportsBarChart.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  date: PropTypes.string.isRequired,
  chart: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.array, PropTypes.object])
  ).isRequired,
};

export default ReportsBarChart;
